/*
 * Featured blog, Editorial layout
 *
 * The arrows step the track one card per click and stop at both ends. This
 * is done here rather than by gridy-slider, the same way as the Editorial
 * carousel: gridy-slider counts slides in items-per-row and wraps around past
 * the last one, so with fixed-width cards and rapid clicks it loops.
 *
 * Each click steps from where the previous click was heading, not from the
 * current scroll position, and the ends are disabled straight away, so rapid
 * clicks can't run past either end. Swiping and dragging update the arrows
 * too, since the state follows scroll.
 */
if (!customElements.get('featured-blog-editorial')) {
  class FeaturedBlogEditorial extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.featured-blog__track');
      this.prevButton = this.querySelector('.featured-blog__controls [data-direction="prev"]');
      this.nextButton = this.querySelector('.featured-blog__controls [data-direction="next"]');
      if (!this.track || !this.prevButton || !this.nextButton) return;

      this.targetLeft = null;
      this.onScroll = this.onScroll.bind(this);
      this.onResize = () => this.updateArrowEnds();
      this.onPrev = () => this.step(-1);
      this.onNext = () => this.step(1);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      this.prevButton.addEventListener('click', this.onPrev);
      this.nextButton.addEventListener('click', this.onNext);
      this.updateArrowEnds();
    }

    disconnectedCallback() {
      if (!this.onScroll) return;
      this.track.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.prevButton.removeEventListener('click', this.onPrev);
      this.nextButton.removeEventListener('click', this.onNext);
      clearTimeout(this.scrollTimer);
    }

    maxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    /* Scroll offset of each card, relative to the first, capped at the end. */
    cardOffsets() {
      const cards = Array.from(this.track.children);
      const first = cards[0] ? cards[0].offsetLeft : 0;
      const max = this.maxScroll();
      return cards.map((card) => Math.min(card.offsetLeft - first, max));
    }

    step(direction) {
      const offsets = this.cardOffsets();
      if (!offsets.length) return;

      const from = this.targetLeft ?? this.track.scrollLeft;
      // The card nearest to where the track is (or is heading).
      let index = 0;
      offsets.forEach((offset, i) => {
        if (Math.abs(offset - from) < Math.abs(offsets[index] - from)) index = i;
      });

      const next = Math.min(Math.max(index + direction, 0), offsets.length - 1);
      this.targetLeft = offsets[next];
      this.track.scrollTo({
        left: this.targetLeft,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
      this.updateArrowEnds(this.targetLeft);
    }

    /* While a click's scroll is running, the arrows reflect where it is
       heading. Once scrolling settles, they follow the real position. */
    onScroll() {
      this.updateArrowEnds(this.targetLeft ?? this.track.scrollLeft);
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        this.targetLeft = null;
        this.updateArrowEnds();
      }, 150);
    }

    /* Previous is disabled at the start, next at the end. */
    updateArrowEnds(position = this.track.scrollLeft) {
      const max = this.maxScroll();
      this.prevButton.disabled = position <= 1;
      this.nextButton.disabled = max <= 1 || position >= max - 1;
    }
  }

  customElements.define('featured-blog-editorial', FeaturedBlogEditorial);
}
